import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, Circle, ChevronDown, ChevronUp, FileText, Truck, ShieldCheck, CreditCard, MapPin, Zap, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "asap_driver_started_collapsed";

export default function DriverGettingStarted({ pendingCount = 0, activeCount = 0 }) {
  const { user, refresh } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "1") setCollapsed(true);
  }, []);

  const loadAll = async () => {
    try {
      const [s, d] = await Promise.all([
        api.get("/stripe/connect/status").catch(() => ({ data: null })),
        api.get("/driver/documents").catch(() => ({ data: [] })),
      ]);
      setStripe(s.data);
      setDocs(d.data);
    } catch {}
  };
  useEffect(() => { loadAll(); const t = setInterval(loadAll, 15000); return () => clearInterval(t); }, []);

  if (!user || user.role !== "driver") return null;

  // Step statuses
  const hasVehicle = !!user.vehicle_category && (user.service_capabilities || []).length > 0;
  const hasLicense = docs.some((d) => d.doc_type === "drivers_license");
  const hasInsurance = docs.some((d) => d.doc_type === "insurance");
  const hasRegistration = docs.some((d) => d.doc_type === "vehicle_registration");
  const approved = user.approval_status === "approved";
  const stripeReady = stripe?.charges_enabled && stripe?.payouts_enabled;
  const stripeStarted = !!stripe?.connected;
  const hasZone = !!user.zone_id;
  const wentOnline = activeCount > 0 || pendingCount > 0 || approved && hasZone;

  const steps = [
    { id: "vehicle", label: "Pick your vehicle & services", done: hasVehicle, icon: Truck, hint: "Below in the sidebar → 'Vehicle & Skills' panel. Choose your truck type and check every service you can perform." },
    { id: "license", label: "Upload driver's license", done: hasLicense, icon: FileText, hint: "Sidebar → 'Onboarding Docs' panel. Paste a link to a photo of your license." },
    { id: "insurance", label: "Upload auto insurance (COI)", done: hasInsurance, icon: ShieldCheck, hint: "Sidebar → 'Onboarding Docs' → pick 'Auto Insurance (COI)' and paste the certificate URL." },
    { id: "registration", label: "Upload vehicle registration", done: hasRegistration, icon: FileText, hint: "Sidebar → 'Onboarding Docs' → pick 'Vehicle Registration' and paste the doc URL." },
    { id: "approved", label: "Get admin approval", done: approved, icon: ShieldCheck, hint: user.approval_status === "pending_review" ? "You're in review! An admin usually approves within a few hours." : "Once all 3 docs are uploaded, admin will approve you shortly." },
    { id: "stripe", label: "Set up Stripe payouts", done: stripeReady, icon: CreditCard, hint: !stripeStarted ? "Sidebar → 'Instant Payouts' panel → 'Set Up Stripe Payouts'. Stripe collects your name, DOB, SSN last 4, bank info." : "Continue Stripe onboarding — a few fields left to fill." },
    { id: "zone", label: "Join a service zone", done: hasZone, icon: MapPin, hint: "Sidebar → 'Service Zone' panel → tap a zone on the map to join. First 7 drivers per zone are free forever." },
    { id: "online", label: "Flip Online to receive jobs", done: wentOnline, icon: Zap, hint: "Once all steps above are green, hit the ONLINE toggle in the top-right header." },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const total = steps.length;
  const pct = Math.round((doneCount / total) * 100);
  const allDone = doneCount === total;

  if (allDone && dismissed) return null;

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch {}
  };

  return (
    <section className="bg-gradient-to-br from-asap-yellow to-asap-orange border-4 border-asap-yellow p-4 sm:p-5 mb-6" data-testid="driver-getting-started">
      <header className="flex items-center gap-3 flex-wrap">
        <div className="w-11 h-11 bg-asap-deep text-asap-yellow flex items-center justify-center border-2 border-asap-deep font-display text-lg shrink-0">
          {doneCount}/{total}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-asap-deep">Getting Started</div>
          <div className="font-display uppercase text-asap-deep text-xl leading-tight">
            {allDone ? "You're all set — go make money!" : "Finish setup to start earning"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {allDone && (
            <button onClick={() => setDismissed(true)} data-testid="dismiss-getting-started" className="p-1.5 border-2 border-asap-deep text-asap-deep hover:bg-asap-deep hover:text-white" aria-label="Dismiss">
              <X size={14}/>
            </button>
          )}
          <button onClick={toggleCollapse} data-testid="toggle-getting-started" className="p-1.5 border-2 border-asap-deep text-asap-deep hover:bg-asap-deep hover:text-white">
            {collapsed ? <ChevronDown size={14}/> : <ChevronUp size={14}/>}
          </button>
        </div>
      </header>

      {/* Progress bar */}
      <div className="mt-3 h-2 bg-asap-deep/20 rounded-full overflow-hidden">
        <div className="h-full bg-asap-deep transition-all" style={{ width: `${pct}%` }}/>
      </div>

      {!collapsed && (
        <ol className="mt-4 space-y-2" data-testid="getting-started-steps">
          {steps.map((s, i) => (
            <li key={s.id} className={`flex items-start gap-3 p-3 border-2 ${s.done ? "border-emerald-500 bg-emerald-50/50" : "border-asap-deep bg-white"}`} data-testid={`step-${s.id}`}>
              <div className={`w-8 h-8 shrink-0 flex items-center justify-center ${s.done ? "bg-emerald-500 text-white" : "bg-asap-deep text-asap-yellow"} font-display`}>
                {s.done ? <CheckCircle2 size={16}/> : (i + 1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-bold text-sm ${s.done ? "text-emerald-800 line-through" : "text-asap-deep"} flex items-center gap-2`}>
                  <s.icon size={14}/> {s.label}
                </div>
                {!s.done && <div className="text-xs text-slate-700 mt-1">{s.hint}</div>}
              </div>
              {s.done && <div className="text-[9px] uppercase tracking-widest font-bold text-emerald-700 shrink-0">Done</div>}
            </li>
          ))}
        </ol>
      )}

      {collapsed && (
        <div className="mt-3 text-xs text-asap-deep font-bold">
          {allDone ? "Everything complete — you're online-ready!" : `${total - doneCount} step${total - doneCount === 1 ? "" : "s"} left. Tap ${collapsed ? "▼" : "▲"} to see what's next.`}
        </div>
      )}
    </section>
  );
}
